/* ═══ SEGMENTS ═══
   A segment is a saved query over the contact book, not a saved list of names.
   This file is the query language and the resolver, and it is the ONLY place
   either one lives: the Segments tab, the Campaigns tab (slice 5) and
   `tools/crm/segment.mjs` all import from here, so the browser and a plain
   node script can only ever resolve the same rules to the same people.

   Pure. No React, no Firestore, no DOM — that is what lets `tools/` import it.

   mockups/crm-v3-campaigns-v1.html, section #segments, is the visual spec;
   this is the arithmetic under it.

   Three ideas hold the whole file up:

   1. **Members ignore consent, eligibility applies it.** `members` is everyone
      the rules match. `eligible` is the subset bulk mail may go to
      (`isBulkEligible`, src/lib/consent.js). `excluded` is the rest, each with
      the reason it was held back. A campaign never sees `members` — but an
      operator has to, because "3 people you cannot email" is a fact about the
      audience they are looking at.

   2. **The consent rule is locked in place, and can only ever narrow.** Every
      segment carries one consent rule. It can never be removed, and its value
      can never be anything but a NON-EMPTY subset of the two bulk-eligible
      states — so "opted in only" is a segment you can build (the mockup's
      "Website opt-ins") and "no bulk too" is not, from any code path. It
      matches nothing during membership; it decides `eligible`. The send-time
      callable applies `isBulkEligible` again regardless, so a tampered record
      can only ever send to fewer people than the rule says, never more.

   3. **Nothing is stored but the rules.** No counts, no member ids. A segment
      recomputes every time it is opened, and a campaign freezes its own copy
      of the resolved list at approval (decision 4). */

import { INDUSTRY_LABELS, CONTACT_ROLES, CONSENT_STATES } from "./constants.js";
import { BULK_ELIGIBLE, isBulkEligible, readConsent } from "./consent.js";
import { contactValueIndex, jobDate, dateOnly, formatMoney } from "./crmValue.js";
import { isRolledUpChangeOrderJob } from "../apps/jobs/lib/revenue.js";
/* The quote-to-contact linkage is listModel's, not a second copy of it: the
   Contacts list and a segment must agree on which quotes reach a person. */
import {
  buildQuoteLookup, isOpenQuote, quoteTotal, lastTouchOf, signedDaysSince,
} from "../apps/crm/listModel.js";

const asArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);
const key = (v) => String(v ?? "").trim().toLowerCase();
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : null);

/**
 * The number a rule is asking for, or null when it is not a usable one.
 *
 * Blank, NaN and negative all come back null, and EVERY caller treats null as
 * "match nobody". That direction is the whole point: a half-typed box or a
 * hand-broken record used to mean "no constraint", which quietly turned a
 * four-rule audience into the whole book — the one failure mode a segment must
 * not have, because the next thing that happens to an audience is a send.
 * Only a number or a numeric string counts. Everything else is null, which
 * matters more than it looks: `Number([])` is 0 and `Number(true)` is 1, so a
 * loose parse reads a broken value as a real threshold instead of refusing it.
 */
const ruleNumber = (v) => {
  if (typeof v === "number") return Number.isFinite(v) && v >= 0 ? v : null;
  if (typeof v !== "string" || !v.trim()) return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
};
/* A month, as the rest of the CRM counts one (listModel.withinMonths). */
const DAYS_PER_MONTH = 30.44;

/* ═══ the grammar ══════════════════════════════════════════════════════════ */

/** The buckets a `value` rule can ask about. `any` is the three added up —
 *  everything this person is attached to, which is what "lifetime value" means
 *  on the Contacts list. */
export const VALUE_BUCKETS = [
  { key: "any", label: "Any" },
  { key: "direct", label: "Direct" },
  { key: "via_company", label: "Via company" },
  { key: "referred", label: "Referred" },
];

/**
 * Every field the rules editor offers, with its ops and the shape of its
 * value. `value` names the control the UI draws:
 *
 *   keys     a multi-select over `options` (industry, role)
 *   tags     free-text tags, comma separated
 *   clients  a multi-select of client ids
 *   contact  one contact id
 *   days     a whole number of days
 *   months   a whole number of months
 *   money    { bucket, amount }
 *   none     the op says everything (LinkedIn)
 *   consent  the locked row; drawn, never edited
 */
export const SEGMENT_FIELDS = [
  {
    key: "industry", label: "Industry", value: "keys", options: INDUSTRY_LABELS,
    ops: [{ key: "any_of", label: "is any of" }],
  },
  {
    key: "role", label: "Role", value: "keys", options: CONTACT_ROLES,
    ops: [{ key: "any_of", label: "is any of" }],
  },
  {
    key: "consent", label: "Consent", value: "consent", locked: true,
    ops: [{ key: "is", label: "is" }],
  },
  {
    key: "tag", label: "Tag", value: "tags",
    ops: [{ key: "any_of", label: "is any of" }, { key: "none_of", label: "is none of" }],
  },
  {
    key: "last_touch", label: "Last touch", value: "days",
    /* "never" takes no value: it is the Contacts list's Never filter, the
       people we hold a record for and have never spoken to. */
    ops: [{ key: "within", label: "within" }, { key: "over", label: "over" }, { key: "never", label: "never" }],
  },
  {
    key: "quote", label: "Quote", value: "months",
    ops: [{ key: "within", label: "in the last" }, { key: "none", label: "none" }],
  },
  {
    key: "job", label: "Job", value: "months",
    ops: [{ key: "within", label: "in the last" }, { key: "none", label: "none" }],
  },
  {
    key: "open_quote", label: "Open quote", value: "days", exclusion: true,
    ops: [{ key: "not_within", label: "not within" }],
  },
  {
    key: "value", label: "Value", value: "money",
    ops: [{ key: "gte", label: "at least" }],
  },
  {
    key: "client", label: "Client", value: "clients",
    ops: [{ key: "any_of", label: "is any of" }],
  },
  {
    key: "referrer", label: "Referrer", value: "contact",
    ops: [{ key: "is", label: "is" }],
  },
  {
    key: "linkedin", label: "LinkedIn", value: "none",
    ops: [{ key: "connected", label: "connected" }, { key: "not_connected", label: "not connected" }],
  },
  {
    key: "created", label: "Created", value: "days",
    ops: [{ key: "within", label: "within" }],
  },
];

export const fieldDef = (field) => SEGMENT_FIELDS.find((f) => f.key === field) || null;

/** The one consent rule every segment carries. Defaults to both bulk-eligible
 *  states, in BULK_ELIGIBLE's order, so the row reads the same everywhere. */
export const CONSENT_RULE = (states) => ({ field: "consent", op: "is", value: consentValue(states) });

export const isConsentRule = (rule) => rule?.field === "consent";

/**
 * A consent rule's value, made safe.
 *
 * Anything outside BULK_ELIGIBLE is dropped rather than rejected, and an empty
 * result falls back to both states. This is the READ path — it runs on records
 * that arrive from Firestore, from a chat-written file and from a half-edited
 * draft — and the one thing it must never do is let `no_bulk` or an
 * unsubscribed state through into an audience. `validateSegment` is where a
 * malformed value gets NAMED; here it just cannot do harm.
 */
export function consentValue(value) {
  const want = new Set(asArray(value).map((v) => String(v)));
  const kept = BULK_ELIGIBLE.filter((s) => want.has(s));
  return kept.length ? kept : [...BULK_ELIGIBLE];
}

/** The consent states this segment's locked row asks for. */
export function consentStates(segment) {
  return consentValue(asArray(segment?.rules).find(isConsentRule)?.value);
}

/** True when the row is the default pair — the case describeSegment leaves
 *  out, because it is on almost every segment and says nothing. */
export const isDefaultConsent = (states) => consentValue(states).length === BULK_ELIGIBLE.length;

/**
 * The rules as the resolver and the editor want them: exactly one consent
 * rule, in canonical form, last.
 *
 * Normalizing rather than validating is deliberate. A segment written by the
 * chat (`tools/crm/segment.mjs --create`) carries only the interesting rules;
 * a segment saved by an older build might carry none. Either way the locked
 * row has to be there, and inventing it here is cheaper than making every
 * caller remember.
 *
 * A narrowed row survives: the first consent rule's value goes through
 * `consentValue()`, which keeps "opted in only" and quietly drops anything a
 * consent rule may not say.
 */
export function normalizeRules(rules) {
  const list = asArray(rules).filter((r) => r && r.field);
  const rest = list.filter((r) => !isConsentRule(r));
  return [...rest, CONSENT_RULE(list.find(isConsentRule)?.value)];
}

/* ═══ the index ════════════════════════════════════════════════════════════ */

/** Every id form a record answers to. */
const allIdsOf = (r) => [r?.id, r?._docId, r?.old_entity_id].filter(Boolean).map(key);

/** When a contact was created. `createdAt` is what new records carry;
 *  `crmOrigin.at` is what the 2026-08-26 ingest repair stamped; `firstContact`
 *  is the oldest form. First one that parses wins. */
export function createdAtOf(contact) {
  return dateOnly(contact?.createdAt) || dateOnly(contact?.crmOrigin?.at) || dateOnly(contact?.firstContact) || "";
}

/**
 * Everything the rules need to know about every contact, in one pass over the
 * collections.
 *
 * Built once and handed to `resolveSegment` through its `index` option, so
 * editing a rule re-filters an array rather than re-walking 400 jobs and 600
 * quotes. `resolveSegment` builds one itself when it is not given one, which is
 * what the node tool and the tests do.
 */
export function buildSegmentIndex({ contacts = [], clients = [], jobs = [], quotes = [] } = {}) {
  const clientsById = new Map();
  for (const cl of clients || []) for (const k of allIdsOf(cl)) clientsById.set(k, cl);

  const contactByAnyId = new Map();
  const contactByName = new Map();
  for (const ct of contacts || []) {
    if (!ct) continue;
    for (const k of allIdsOf(ct)) contactByAnyId.set(k, key(ct.id));
    const n = key(ct.name);
    if (n && !contactByName.has(n)) contactByName.set(n, key(ct.id));
  }

  /* Employer ids per contact, each expanded to every id form the client
     answers to, so a job pointing at a legacy entity id still lands. */
  const facts = new Map();
  const contactsByClientId = new Map();
  for (const ct of contacts || []) {
    if (!ct) continue;
    const k = key(ct.id);
    /* Two sets, because they answer two different questions. `clientIds` holds
       EVERY id form an employer answers to, so a `client` rule and a job
       pointing at a legacy entity id both land. `clientKeys` holds one id per
       employer, so counting them counts accounts — the alias set would report
       a single client with an `_docId` and an `old_entity_id` as three. */
    const clientIds = new Set();
    const clientKeys = new Set();
    for (const raw of [ct.clientId, ...asArray(ct.linkedClients).map((l) => l?.clientId)].filter(Boolean)) {
      clientIds.add(key(raw));
      const cl = clientsById.get(key(raw));
      if (cl) allIdsOf(cl).forEach((v) => clientIds.add(v));
      // An unresolved link is still an employer; its own id is the best key.
      clientKeys.add(key(cl?.id || raw));
    }
    for (const id of clientIds) {
      if (!contactsByClientId.has(id)) contactsByClientId.set(id, new Set());
      contactsByClientId.get(id).add(k);
    }
    const roles = asArray(ct.role).length ? asArray(ct.role) : ["prospect"];
    const linkedin = ct.social?.linkedin || {};
    facts.set(k, {
      id: ct.id,
      contact: ct,
      clientIds,
      clientKeys,
      roles: new Set(roles.map(key)),
      industry: new Set(asArray(ct.industry).map(key)),
      tags: new Set(asArray(ct.tags).map(key)),
      consent: readConsent(ct),
      eligible: isBulkEligible(ct?.consent),
      touchAt: lastTouchOf(ct).at,
      createdAt: createdAtOf(ct),
      linkedin: linkedin?.connected === true,
      jobDates: [],
      quoteDates: [],
      openQuotes: [],
      referredBy: key(ct.referredBy),
    });
  }

  /* Who sent us whom, by id or by the typed name. Same rule crmValue.js uses
     for the referred bucket, so "referrer is Ben" and "Ben's referred money"
     are talking about the same set of records. */
  const referrerOf = new Map();
  for (const ct of contacts || []) {
    const v = key(ct?.referredBy);
    if (!v) continue;
    const referrer = contactByAnyId.get(v) || contactByName.get(v) || "";
    if (referrer && referrer !== key(ct.id)) referrerOf.set(key(ct.id), referrer);
  }
  const referrerOfClient = new Map();
  for (const cl of clients || []) {
    const v = key(cl?.referredBy);
    if (!v) continue;
    const referrer = contactByAnyId.get(v) || contactByName.get(v) || "";
    if (referrer) for (const k of allIdsOf(cl)) referrerOfClient.set(k, referrer);
  }

  /* ── jobs ──
     A job reaches a contact two ways: it names them, or it belongs to their
     employer. A referred job does NOT count as this person having a job with
     us — it is someone else's job — so "no job" on a referrer stays true. */
  const liveJobs = (jobs || []).filter((j) => j && !j.deletedAt);
  for (const j of liveJobs) {
    if (isRolledUpChangeOrderJob(j, liveJobs)) continue;
    const date = jobDate(j);
    const linkIds = [
      ...asArray(j.linkedContacts).map((l) => key(l?.contactId)),
      ...asArray(j.linkedEntities).map((l) => key(l?.entityId)),
    ].filter(Boolean);
    const ownerIds = [
      key(j.clientId),
      ...asArray(j.linkedEntities).map((l) => key(l?.entityId)),
    ].filter(Boolean);
    const hit = new Set();
    for (const id of linkIds) { const k = contactByAnyId.get(id); if (k) hit.add(k); }
    for (const id of ownerIds) for (const k of contactsByClientId.get(id) || []) hit.add(k);
    for (const k of hit) facts.get(k)?.jobDates.push(date);
  }

  /* ── quotes ──
     listModel's lookup, so the quotes a segment can see are exactly the quotes
     the Contacts list counts: the snapshotted contactName, plus every client
     the person is linked to. */
  const lookup = buildQuoteLookup(quotes, clients);
  for (const f of facts.values()) {
    const reachable = [
      ...(lookup.byContactName.get(key(f.contact.name)) || []),
      ...[...f.clientIds].flatMap((id) => lookup.byClientId.get(id) || []),
    ];
    const seen = new Set();
    for (const q of reachable) {
      if (!q || seen.has(q.id)) continue;
      seen.add(q.id);
      const date = dateOnly(q.quoteDate) || dateOnly(q.savedAt) || dateOnly(q.createdAt);
      f.quoteDates.push(date);
      if (isOpenQuote(q)) f.openQuotes.push({ id: q.id, date, total: quoteTotal(q) });
    }
  }

  return {
    facts,
    referrerOf,
    referrerOfClient,
    /* Money is the expensive part and only a `value` rule needs it, so it is
       built on first ask and kept. A segment with no value rule never pays. */
    _values: null,
    values() {
      if (!this._values) this._values = contactValueIndex({ contacts, jobs, clients, quotes });
      return this._values;
    },
  };
}

/* ═══ matching ═════════════════════════════════════════════════════════════ */

/**
 * Days between a stored date and now, or null when there is no date.
 *
 * Whole CALENDAR days, which is why the time is thrown away first: a touch
 * stamped 15:00 today, read at 09:00 today, is 0 days old and not a record
 * from the future. Only a date genuinely ahead of today comes back negative,
 * and every window below refuses those — a job booked for next March is not a
 * contact we spoke to this week.
 */
const daysBetween = (at, now) => signedDaysSince(dateOnly(at) || at, now);

const withinDays = (at, days, now) => {
  const d = daysBetween(at, now);
  return d !== null && d >= 0 && d <= days;
};

const withinMonthsOf = (at, months, now) => withinDays(at, Math.round(months * DAYS_PER_MONTH), now);

/** The value one bucket of a rule asks about. */
function bucketAmount(buckets, bucket) {
  const b = buckets || {};
  const direct = Number(b.direct?.amount) || 0;
  const via = Number(b.viaCompany?.amount) || 0;
  const referred = Number(b.referred?.amount) || 0;
  if (bucket === "direct") return direct;
  if (bucket === "via_company") return via;
  if (bucket === "referred") return referred;
  return direct + via + referred;
}

/**
 * Does one contact match one rule?
 *
 * The consent rule always matches: membership ignores consent by design (see
 * the header), and eligibility is applied to every segment afterwards whether
 * the rule is there or not.
 */
export function matchesRule(facts, rule, ctx) {
  const { index, now } = ctx;
  const value = rule?.value;

  /* An op the field does not declare matches nobody. Every branch below reads
     `rule.op` as one of two known values and falls through to the other, so a
     hand-edited `{field:"linkedin", op:"conected"}` would silently mean
     "connected" — a typo widening an outreach audience. The editor cannot
     produce one (validateSegment refuses it), which is exactly why the
     resolver has to refuse it too. */
  const def = fieldDef(rule?.field);
  if (!def || !def.ops.some((o) => o.key === rule?.op)) return false;

  switch (rule?.field) {
    case "consent":
      return true;

    case "industry":
      return asArray(value).some((v) => facts.industry.has(key(v)));

    case "role":
      return asArray(value).some((v) => facts.roles.has(key(v)));

    case "tag": {
      const hit = asArray(value).some((v) => facts.tags.has(key(v)));
      return rule.op === "none_of" ? !hit : hit;
    }

    case "last_touch": {
      const since = daysBetween(facts.touchAt, now);
      /* A contact who has never been touched matches neither window. "Over 180
         days" is a claim about a date we have; absence is not a long silence,
         it is no record — and putting people we know nothing about into an
         outreach audience is how a segment stops being trustworthy. It IS its
         own question, which is what the `never` op asks. */
      if (rule.op === "never") return since === null;
      const days = ruleNumber(value);
      if (days === null) return false;
      if (since === null || since < 0) return false;
      // "Over 180 days" is strictly older than 180 days. Day 180 itself is the
      // last day of "within 180", and no date can be in both windows at once.
      return rule.op === "over" ? since > days : since <= days;
    }

    case "created": {
      const days = ruleNumber(value);
      if (days === null) return false;
      return withinDays(facts.createdAt, days, now);
    }

    case "quote": {
      if (rule.op === "none") return facts.quoteDates.length === 0;
      const months = ruleNumber(value);
      if (months === null) return false;
      return facts.quoteDates.some((d) => withinMonthsOf(d, months, now));
    }

    case "job": {
      if (rule.op === "none") return facts.jobDates.length === 0;
      const months = ruleNumber(value);
      if (months === null) return false;
      return facts.jobDates.some((d) => withinMonthsOf(d, months, now));
    }

    case "open_quote": {
      // An exclusion: keep the people who do NOT have a live quote that recent.
      // A broken window matches nobody rather than excluding nobody — the
      // conservative direction for an exclusion is the empty audience, not the
      // one where the exclusion silently stopped applying.
      const days = ruleNumber(value);
      if (days === null) return false;
      return !facts.openQuotes.some((q) => withinDays(q.date, days, now));
    }

    case "value": {
      const amount = ruleNumber(value?.amount);
      if (amount === null) return false;
      const bucket = value?.bucket || "any";
      // An unknown bucket would fall through to "any", which is the three
      // added up and so the widest reading of the rule. Refuse it instead.
      if (!VALUE_BUCKETS.some((b) => b.key === bucket)) return false;
      return bucketAmount(index.values().get(key(facts.id)), bucket) >= amount;
    }

    case "client":
      return asArray(value).some((id) => facts.clientIds.has(key(id)));

    case "referrer": {
      const who = key(value);
      // Nobody chosen is not "anybody referred": it is a rule that has not been
      // filled in, and an unfilled rule may not hand back the whole book.
      if (!who) return false;
      if (index.referrerOf.get(key(facts.id)) === who) return true;
      for (const id of facts.clientIds) if (index.referrerOfClient.get(id) === who) return true;
      return false;
    }

    case "linkedin":
      return rule.op === "not_connected" ? !facts.linkedin : facts.linkedin;

    default:
      // An unknown field cannot be honoured, and silently ignoring it would
      // widen the audience. It matches nobody; validateSegment names it.
      return false;
  }
}

/* ═══ the resolver ═════════════════════════════════════════════════════════ */

/**
 * Why a member cannot be sent to. "" when they can.
 *
 * `states` is the segment's own consent row. Somebody held back by a NARROWED
 * row is a different fact from somebody who was never bulk-eligible at all —
 * "Not opted in" is a person we could write to, on a list that asked for
 * people who put their hand up — so the two read differently.
 */
export function exclusionReason(contact, states = BULK_ELIGIBLE) {
  const c = readConsent(contact);
  if (c.unsubscribed) return "Unsubscribed";
  if (!BULK_ELIGIBLE.includes(c.state)) return CONSENT_STATES.no_bulk;
  const asked = consentValue(states);
  if (!asked.includes(c.state)) return `Not ${asked.map((s) => CONSENT_STATES[s].toLowerCase()).join(" or ")}`;
  return "";
}

const TOUCH_VERBS = {
  quote: "quoted", job: "job", mail_in: "emailed", mail_out: "wrote",
  campaign: "campaign", note: "noted", session: "session",
};

/* "Sep 3" this year, "Sep 24, 2025" in any other. Without the year, a touch
   from three years ago reads as a fortnight ago — and this line is the only
   thing on the row saying when anything last happened. */
const shortDate = (at, now) => {
  const iso = dateOnly(at);
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return "";
  const sameYear = d.getUTCFullYear() === (now instanceof Date ? now : new Date(now)).getFullYear();
  return d.toLocaleDateString("en-US", {
    month: "short", day: "numeric", timeZone: "UTC", ...(sameYear ? {} : { year: "numeric" }),
  });
};

/**
 * The one-line "why this person is here" the sample list shows —
 * "quoted Sep 3", "cc on a thread".
 *
 * It reads the record, not the rules: what an operator scanning five names
 * wants is the last thing that happened with this person, and for a cc'd
 * record (which is most of the No bulk rows) the fact that they arrived as a
 * cc is the whole story.
 */
export function memberReason(contact, now = new Date()) {
  const touch = lastTouchOf(contact);
  const when = shortDate(touch.at, now);
  const verb = TOUCH_VERBS[touch.channel];
  if (verb && when) return `${verb} ${when}`;
  if (key(contact?.crmOrigin?.role) === "cc") return "cc on a thread";
  if (when) return `last touch ${when}`;
  return "";
}

/**
 * Resolve a segment against the collections.
 *
 * @param segment            { name, rules }
 * @param o.contacts         every contact
 * @param o.clients          every client
 * @param o.jobs             every job
 * @param o.quotes           every quote
 * @param o.now              the clock, for the date windows (default: now)
 * @param o.index            a prebuilt buildSegmentIndex(), optional
 * @returns { members, eligible, excluded, excludedBy, counts }
 *
 * `members` is everyone the rules match, newest touch first — the order the
 * sample list wants and the order the full list opens in. `eligible` and
 * `excluded` are subsets of it in the same order. `excludedBy` maps a contact
 * id to the reason it was held back.
 *
 * `eligible` is `isBulkEligible()` AND the segment's own consent row, so a
 * narrowed row ("opted in only") holds more people back and a tampered one
 * cannot let anybody through: the first test caps the second.
 *
 * A segment whose RULES do not validate resolves to nobody, with the problems
 * on `problems`. Individual rules already refuse a malformed value one by one;
 * this is the same answer stated once, so a caller that never looks at a rule
 * (a campaign resolving a stored segment) cannot be handed an audience built
 * out of a rule nobody could read. The NAME is not checked here — an unnamed
 * draft is exactly what the editor is showing you the audience of.
 */
export function resolveSegment(segment, { contacts = [], clients = [], jobs = [], quotes = [], now = new Date(), index } = {}) {
  const idx = index || buildSegmentIndex({ contacts, clients, jobs, quotes });
  const rules = asArray(segment?.rules).filter((r) => r && r.field);
  const ctx = { index: idx, now };

  const problems = ruleProblems(segment?.rules);
  if (problems.length) return { ...emptyResolution(), problems };

  const asked = consentStates(segment);
  const members = [];
  for (const facts of idx.facts.values()) {
    let ok = true;
    for (const rule of rules) {
      if (!matchesRule(facts, rule, ctx)) { ok = false; break; }
    }
    if (ok) members.push(facts);
  }

  members.sort((a, b) =>
    (b.touchAt || "").localeCompare(a.touchAt || "") ||
    key(a.contact.name).localeCompare(key(b.contact.name)));

  const eligible = [];
  const excluded = [];
  const excludedBy = {};
  const counts = {
    total: members.length, opted_in: 0, relationship: 0, no_bulk: 0, unsubscribed: 0,
    clients: 0, quotedValue: 0,
  };
  const clientIds = new Set();
  const quotedSeen = new Set();

  for (const f of members) {
    if (f.eligible && asked.includes(f.consent.state)) eligible.push(f.contact);
    else {
      excluded.push(f.contact);
      excludedBy[f.contact.id] = exclusionReason(f.contact, asked);
    }
    /* The consent split counts an unsubscribe as its own answer rather than
       folding it into No bulk — the same call the Contacts list makes. */
    if (f.consent.unsubscribed) counts.unsubscribed += 1;
    else if (f.consent.state === "opted_in") counts.opted_in += 1;
    else if (f.consent.state === "relationship") counts.relationship += 1;
    else counts.no_bulk += 1;

    for (const id of f.clientKeys) clientIds.add(id);
    for (const q of f.openQuotes) {
      // Deduped by quote id: two people at the same account are not two quotes.
      if (quotedSeen.has(q.id)) continue;
      quotedSeen.add(q.id);
      counts.quotedValue += q.total;
    }
  }
  counts.clients = clientIds.size;

  return { members: members.map((f) => f.contact), eligible, excluded, excludedBy, counts, problems };
}

/** Nobody, in the shape every caller already reads. */
function emptyResolution() {
  return {
    members: [], eligible: [], excluded: [], excludedBy: {},
    counts: {
      total: 0, opted_in: 0, relationship: 0, no_bulk: 0, unsubscribed: 0,
      clients: 0, quotedValue: 0,
    },
  };
}

/* ═══ describe ═════════════════════════════════════════════════════════════ */

const labelsFor = (map, values) => asArray(values).map((v) => map[v] || v);

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * The one-line definition the list row and the campaign brief show:
 * "Film / TV · quote in last 12 mo · no job · not open quote < 30 d".
 *
 * The consent rule is left out. It is on every segment, so printing it on
 * every line says nothing; the detail panel draws the locked row instead.
 *
 * @param segment
 * @param o.clients   optional, so a `client` rule can name the account
 * @param o.contacts  optional, so a `referrer` rule can name the person
 */
export function describeSegment(segment, { clients = [], contacts = [] } = {}) {
  const nameOf = (list, id) => (list || []).find((r) => key(r?.id) === key(id))?.name || "";
  const parts = [];
  for (const rule of asArray(segment?.rules)) {
    if (!rule || !rule.field) continue;
    /* The default pair is on nearly every segment, so printing it says
       nothing; a NARROWED row is the whole point of the segment and has to be
       on the line. */
    if (isConsentRule(rule)) {
      const states = consentValue(rule.value);
      if (!isDefaultConsent(states)) {
        parts.push(`consent ${states.map((s) => CONSENT_STATES[s].toLowerCase()).join(" or ")}`);
      }
      continue;
    }
    const v = rule.value;
    switch (rule.field) {
      case "industry":
        parts.push(labelsFor(INDUSTRY_LABELS, v).join(" or "));
        break;
      case "role":
        parts.push(labelsFor(CONTACT_ROLES, v).join(" or "));
        break;
      case "tag":
        parts.push(`${rule.op === "none_of" ? "not tagged" : "tag"} ${asArray(v).join(", ")}`);
        break;
      case "last_touch":
        parts.push(rule.op === "never"
          ? "never touched"
          : `last touch ${rule.op === "over" ? "over" : "within"} ${num(v) ?? "?"} d`);
        break;
      case "created":
        parts.push(`added in last ${num(v) ?? "?"} d`);
        break;
      case "quote":
        parts.push(rule.op === "none" ? "no quote" : `quote in last ${num(v) ?? "?"} mo`);
        break;
      case "job":
        parts.push(rule.op === "none" ? "no job" : `job in last ${num(v) ?? "?"} mo`);
        break;
      case "open_quote":
        parts.push(`not open quote < ${num(v) ?? "?"} d`);
        break;
      case "value": {
        const bucket = VALUE_BUCKETS.find((b) => b.key === (v?.bucket || "any"));
        const label = bucket && bucket.key !== "any" ? `${bucket.label.toLowerCase()} value` : "value";
        parts.push(`${label} ${formatMoney(num(v?.amount) ?? 0) || "$0"}+`);
        break;
      }
      case "client": {
        const named = asArray(v).map((id) => nameOf(clients, id)).filter(Boolean);
        parts.push(named.length === asArray(v).length && named.length
          ? `client ${named.join(" or ")}`
          : plural(asArray(v).length, "client", "clients"));
        break;
      }
      case "referrer": {
        const named = nameOf(contacts, v);
        parts.push(named ? `referred by ${named}` : "referred by a contact");
        break;
      }
      case "linkedin":
        parts.push(rule.op === "not_connected" ? "not connected on LinkedIn" : "connected on LinkedIn");
        break;
      default:
        parts.push(`unknown rule ${rule.field}`);
    }
  }
  return parts.filter(Boolean).join(" · ") || "Everyone";
}

/* ═══ validate ═════════════════════════════════════════════════════════════ */

const isWholeNumber = (v) => Number.isFinite(Number(v)) && Number(v) > 0 && Number.isInteger(Number(v));

/**
 * What is wrong with this segment, as plain sentences. `[]` means it is
 * saveable.
 *
 * It refuses on shape, never on size: a segment that matches nobody today is
 * still a legitimate audience (that is the point of a live query), and a rule
 * with no value is not.
 */
export function validateSegment(segment) {
  const problems = [];
  if (!String(segment?.name || "").trim()) problems.push("The segment needs a name.");
  return [...problems, ...ruleProblems(segment?.rules)];
}

/**
 * Everything wrong with the RULES alone — the half of `validateSegment` that
 * decides who is in the audience.
 *
 * Split out because `resolveSegment` runs it and a name is not an audience
 * question: the editor has to show you the counts for the segment you are
 * about to name, and a draft called "" is not a reason to say nobody matches.
 */
export function ruleProblems(rules) {
  const problems = [];
  if (rules !== undefined && !Array.isArray(rules)) {
    problems.push("rules must be a list.");
    return problems;
  }

  const seen = new Set();
  asArray(rules).forEach((rule, i) => {
    const where = `Rule ${i + 1}`;
    if (!rule || typeof rule !== "object") { problems.push(`${where} is not a rule.`); return; }
    const def = fieldDef(rule.field);
    if (!def) { problems.push(`${where}: "${rule.field}" is not a field a segment can ask about.`); return; }
    if (!def.ops.some((o) => o.key === rule.op)) {
      problems.push(`${where}: ${def.label} cannot be "${rule.op}". Valid: ${def.ops.map((o) => o.key).join(", ")}.`);
      return;
    }
    const pair = `${rule.field}|${rule.op}`;
    if (seen.has(pair)) problems.push(`${where}: a second "${def.label} ${rule.op}" rule — combine them into one.`);
    seen.add(pair);

    const v = rule.value;
    if (def.key === "consent") {
      const states = asArray(v).map(String);
      const widened = states.filter((s) => !BULK_ELIGIBLE.includes(s));
      if (widened.length) {
        problems.push(`${where}: the consent rule may only narrow — ${widened.join(", ")} is not one of ${BULK_ELIGIBLE.join(" or ")}.`);
      } else if (!states.length) {
        problems.push(`${where}: the consent rule needs at least one of ${BULK_ELIGIBLE.join(" or ")}.`);
      }
      return;
    }
    if (def.value === "none") return;
    if (rule.field === "quote" || rule.field === "job") {
      if (rule.op === "none") return;
      if (!isWholeNumber(v)) problems.push(`${where}: ${def.label} needs a whole number of months.`);
      return;
    }
    // "Last touch never" asks about the absence of a date; there is no number.
    if (rule.op === "never") return;
    if (def.value === "days") {
      if (!isWholeNumber(v)) problems.push(`${where}: ${def.label} needs a whole number of days.`);
      return;
    }
    if (def.value === "money") {
      const amount = num(v?.amount);
      if (amount === null || amount < 0) problems.push(`${where}: ${def.label} needs a dollar amount.`);
      if (v?.bucket && !VALUE_BUCKETS.some((b) => b.key === v.bucket)) {
        problems.push(`${where}: "${v.bucket}" is not a value bucket.`);
      }
      return;
    }
    if (def.value === "contact") {
      if (!String(v || "").trim()) problems.push(`${where}: ${def.label} needs a contact.`);
      return;
    }
    // keys, tags, clients: a multi-select with nothing chosen matches nobody.
    if (!asArray(v).length) {
      problems.push(`${where}: ${def.label} needs at least one value.`);
      return;
    }
    if (def.options) {
      const bad = asArray(v).filter((x) => !Object.prototype.hasOwnProperty.call(def.options, x));
      if (bad.length) problems.push(`${where}: ${def.label} does not have ${bad.join(", ")}.`);
    }
  });

  return problems;
}
